import React, { useState, useEffect } from 'react';
import { FaUniversalAccess, FaTimes, FaTextHeight, FaAdjust, FaPalette, FaKeyboard, FaMousePointer, FaEye, FaFont, FaLink, FaRedo } from 'react-icons/fa';
import './AccessibilityMenu.css';

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    lineHeight: 100,
    textSpacing: 100,
    cursorSize: 100,
    highlightLinks: false,
    highlightHeadings: false,
    readableFont: false,
    textAlign: 'right',
    grayscale: false,
    highContrast: false,
    invertColors: false,
    lowSaturation: false,
    lightBackground: false,
    darkBackground: false,
    keyboardNav: false
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (newSettings) => {
    const root = document.documentElement;
    const body = document.body;
    const app = document.querySelector('.App');

    root.style.fontSize = `${newSettings.fontSize}%`;
    body.style.lineHeight = `${newSettings.lineHeight / 100 * 1.7}`;
    body.style.letterSpacing = newSettings.textSpacing > 100 ? `${(newSettings.textSpacing - 100) / 100}px` : 'normal';

    if (newSettings.cursorSize > 100) {
      body.style.cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${newSettings.cursorSize / 10}" height="${newSettings.cursorSize / 10}"><circle cx="${newSettings.cursorSize / 20}" cy="${newSettings.cursorSize / 20}" r="${newSettings.cursorSize / 20}" fill="black" stroke="white" stroke-width="1"/></svg>') ${newSettings.cursorSize / 20} ${newSettings.cursorSize / 20}, auto`;
    } else {
      body.style.cursor = 'default';
    }

    body.classList.toggle('highlight-links', newSettings.highlightLinks);
    body.classList.toggle('highlight-headings', newSettings.highlightHeadings);
    body.classList.toggle('readable-font', newSettings.readableFont);
    body.classList.toggle('light-background-mode', newSettings.lightBackground);
    body.classList.toggle('dark-background-mode', newSettings.darkBackground);
    body.classList.toggle('keyboard-nav-mode', newSettings.keyboardNav);

    body.style.textAlign = newSettings.textAlign;

    if (app) {
      let filters = [];
      if (newSettings.grayscale) filters.push('grayscale(100%)');
      if (newSettings.highContrast) filters.push('contrast(150%)');
      if (newSettings.invertColors) filters.push('invert(1) hue-rotate(180deg)');
      if (newSettings.lowSaturation) filters.push('saturate(0.3)');
      
      app.style.filter = filters.length > 0 ? filters.join(' ') : 'none';
    }
  };

  const adjustSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      lineHeight: 100,
      textSpacing: 100,
      cursorSize: 100,
      highlightLinks: false,
      highlightHeadings: false,
      readableFont: false,
      textAlign: 'right',
      grayscale: false,
      highContrast: false,
      invertColors: false,
      lowSaturation: false,
      lightBackground: false,
      darkBackground: false,
      keyboardNav: false
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen]);

  return (
    <>
      <button
        className="accessibility-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="פתח תפריט נגישות"
        title="נגישות"
      >
        <FaUniversalAccess />
      </button>

      {isOpen && (
        <div className="accessibility-menu" role="dialog" aria-label="תפריט נגישות">
          <div className="accessibility-menu-header">
            <h2>
              <FaUniversalAccess /> הצהרת נגישות
            </h2>
            <button
              className="close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="סגור תפריט נגישות"
            >
              <FaTimes />
            </button>
          </div>

          <div className="accessibility-menu-content">
            <div className="accessibility-section">
              <h3><FaTextHeight /> התאמת טקסט</h3>
              
              <div className="setting-item">
                <label>גודל טקסט</label>
                <div className="slider-controls">
                  <button onClick={() => adjustSetting('fontSize', Math.max(80, settings.fontSize - 10))}>-</button>
                  <input
                    type="range"
                    min="80"
                    max="200"
                    step="10"
                    value={settings.fontSize}
                    onChange={(e) => adjustSetting('fontSize', parseInt(e.target.value))}
                    aria-label="התאם גודל טקסט"
                  />
                  <button onClick={() => adjustSetting('fontSize', Math.min(200, settings.fontSize + 10))}>+</button>
                  <span>{settings.fontSize}%</span>
                </div>
              </div>

              <div className="setting-item">
                <label>גובה שורה</label>
                <div className="slider-controls">
                  <button onClick={() => adjustSetting('lineHeight', Math.max(80, settings.lineHeight - 10))}>-</button>
                  <input
                    type="range"
                    min="80"
                    max="200"
                    step="10"
                    value={settings.lineHeight}
                    onChange={(e) => adjustSetting('lineHeight', parseInt(e.target.value))}
                    aria-label="התאם גובה שורה"
                  />
                  <button onClick={() => adjustSetting('lineHeight', Math.min(200, settings.lineHeight + 10))}>+</button>
                  <span>{settings.lineHeight}%</span>
                </div>
              </div>

              <div className="setting-item">
                <label>ריווח טקסט</label>
                <div className="slider-controls">
                  <button onClick={() => adjustSetting('textSpacing', Math.max(80, settings.textSpacing - 10))}>-</button>
                  <input
                    type="range"
                    min="80"
                    max="200"
                    step="10"
                    value={settings.textSpacing}
                    onChange={(e) => adjustSetting('textSpacing', parseInt(e.target.value))}
                    aria-label="התאם ריווח טקסט"
                  />
                  <button onClick={() => adjustSetting('textSpacing', Math.min(200, settings.textSpacing + 10))}>+</button>
                  <span>{settings.textSpacing}%</span>
                </div>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.readableFont ? 'active' : ''}`}
                  onClick={() => toggleSetting('readableFont')}
                >
                  <FaFont /> גופן קריא
                </button>
              </div>

              <div className="setting-item">
                <label>יישור טקסט</label>
                <div className="button-group">
                  <button
                    className={settings.textAlign === 'right' ? 'active' : ''}
                    onClick={() => adjustSetting('textAlign', 'right')}
                  >
                    ימין
                  </button>
                  <button
                    className={settings.textAlign === 'center' ? 'active' : ''}
                    onClick={() => adjustSetting('textAlign', 'center')}
                  >
                    מרכז
                  </button>
                  <button
                    className={settings.textAlign === 'left' ? 'active' : ''}
                    onClick={() => adjustSetting('textAlign', 'left')}
                  >
                    שמאל
                  </button>
                </div>
              </div>
            </div>

            <div className="accessibility-section">
              <h3><FaPalette /> צבעים וניגודיות</h3>
              
              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.highContrast ? 'active' : ''}`}
                  onClick={() => toggleSetting('highContrast')}
                >
                  <FaAdjust /> ניגודיות גבוהה
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.invertColors ? 'active' : ''}`}
                  onClick={() => toggleSetting('invertColors')}
                >
                  <FaPalette /> היפוך צבעים
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.grayscale ? 'active' : ''}`}
                  onClick={() => toggleSetting('grayscale')}
                >
                  <FaEye /> גווני אפור
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.lowSaturation ? 'active' : ''}`}
                  onClick={() => toggleSetting('lowSaturation')}
                >
                  <FaPalette /> רוויה נמוכה
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.lightBackground ? 'active' : ''}`}
                  onClick={() => {
                    adjustSetting('lightBackground', !settings.lightBackground);
                    if (!settings.lightBackground) {
                      adjustSetting('darkBackground', false);
                    }
                  }}
                >
                  רקע בהיר
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.darkBackground ? 'active' : ''}`}
                  onClick={() => {
                    adjustSetting('darkBackground', !settings.darkBackground);
                    if (!settings.darkBackground) {
                      adjustSetting('lightBackground', false);
                    }
                  }}
                >
                  רקע כהה
                </button>
              </div>
            </div>

            <div className="accessibility-section">
              <h3><FaMousePointer /> ניווט והדגשות</h3>

              <div className="setting-item">
                <label>גודל סמן עכבר</label>
                <div className="slider-controls">
                  <button onClick={() => adjustSetting('cursorSize', Math.max(100, settings.cursorSize - 20))}>-</button>
                  <input
                    type="range"
                    min="100"
                    max="300"
                    step="20"
                    value={settings.cursorSize}
                    onChange={(e) => adjustSetting('cursorSize', parseInt(e.target.value))}
                    aria-label="התאם גודל סמן"
                  />
                  <button onClick={() => adjustSetting('cursorSize', Math.min(300, settings.cursorSize + 20))}>+</button>
                  <span>{settings.cursorSize}%</span>
                </div>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.highlightLinks ? 'active' : ''}`}
                  onClick={() => toggleSetting('highlightLinks')}
                >
                  <FaLink /> הדגש קישורים
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.highlightHeadings ? 'active' : ''}`}
                  onClick={() => toggleSetting('highlightHeadings')}
                >
                  <FaTextHeight /> הדגש כותרות
                </button>
              </div>

              <div className="setting-item toggle-item">
                <button
                  className={`toggle-btn ${settings.keyboardNav ? 'active' : ''}`}
                  onClick={() => toggleSetting('keyboardNav')}
                >
                  <FaKeyboard /> ניווט מקלדת
                </button>
              </div>
            </div>
          </div>

          <div className="accessibility-menu-footer">
            <button className="reset-btn" onClick={resetSettings}>
              <FaRedo /> אפס הגדרות
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityMenu;

